import { useState, type FormEvent } from 'react';
import { useManualPayment } from '../api/orders.js';
import { useCurrentUser } from '../auth/session.js';
import { ErrorText } from '../components/ErrorText.js';
import { parseMoneyInput } from '../format.js';
import { t } from '../i18n/uk.js';

const tp = t.payments;

/** A button that first asks for a reason, then runs the action with it. */
export function ReasonAction({
  label,
  hint,
  pending,
  error,
  onConfirm,
  danger = false,
}: {
  label: string;
  hint?: string;
  pending: boolean;
  error: unknown;
  onConfirm: (reason: string, done: () => void) => void;
  danger?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <button type="button" className={danger ? 'danger' : undefined} onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  }
  return (
    <div className="inline-form reason-action">
      {hint && <p className="muted hint">{hint}</p>}
      <input
        aria-label={`${t.orders.reason}: ${label}`}
        placeholder={t.orders.reason}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="grow"
      />
      <button
        type="button"
        className={danger ? 'danger' : 'primary'}
        disabled={!reason.trim() || pending}
        onClick={() => onConfirm(reason, () => setOpen(false))}
      >
        {label}
      </button>
      <button type="button" onClick={() => setOpen(false)}>
        {t.common.cancel}
      </button>
      <ErrorText error={error} />
    </div>
  );
}

/**
 * PAY-03: money typed in by staff — received (`payments.match`) or refunded (`payments.refund`).
 * `orderId` comes from the order page, or is looked up by number on the payments page.
 */
export function ManualPaymentForm({
  orderId,
  defaultMethod = 'bank_transfer',
  onDone,
}: {
  orderId: string;
  defaultMethod?: 'card' | 'bank_transfer' | 'cod';
  onDone?: () => void;
}) {
  const me = useCurrentUser();
  const canIn = me.permissions.includes('payments.match');
  const canRefund = me.permissions.includes('payments.refund');
  const save = useManualPayment();
  const empty = { amount: '', method: defaultMethod, reference: '', reason: '', refund: !canIn };
  const [form, setForm] = useState(empty);
  const [invalid, setInvalid] = useState(false);
  if (!canIn && !canRefund) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const amount = parseMoneyInput(form.amount);
    if (amount === null || amount <= 0) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    save.mutate(
      {
        orderId,
        amount,
        method: form.method,
        reference: form.reference.trim() || null,
        reason: form.reason,
        refund: form.refund,
      },
      {
        onSuccess: () => {
          setForm(empty);
          onDone?.();
        },
      },
    );
  };

  return (
    <form className="inline-form" onSubmit={submit}>
      <label>
        {tp.amount}
        <input
          aria-label={tp.amount}
          inputMode="decimal"
          required
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
        />
      </label>
      <label>
        {tp.method}
        <select
          aria-label={tp.method}
          value={form.method}
          onChange={(e) => setForm({ ...form, method: e.target.value as typeof form.method })}
        >
          {Object.entries(t.orders.methods).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label>
        {tp.reference}
        <input
          aria-label={tp.reference}
          value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
        />
      </label>
      <label className="grow">
        {tp.reason}
        <input
          aria-label={tp.reason}
          required
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
      </label>
      {canIn && canRefund && (
        <label className="check">
          <input
            type="checkbox"
            checked={form.refund}
            onChange={(e) => setForm({ ...form, refund: e.target.checked })}
          />
          {tp.isRefund}
        </label>
      )}
      <button type="submit" disabled={save.isPending}>
        {form.refund ? tp.refund : tp.record}
      </button>
      {invalid && <span className="error-text">{t.common.invalidAmount}</span>}
      <ErrorText error={save.error} />
    </form>
  );
}
